/**
 * The first run's app picks (RFC 0279 S6): the registry's Featured list, and
 * installing the ticked ones into the World (or the project, when this window
 * has no World pointer). OnboardingFlow uses these unless a test hands it fakes.
 */

import type { Repo } from '@automerge/automerge-repo'
import {
  installRelease,
  type RegistryEntry,
  registryClient,
} from '@mod/core/model'
import { readWorldPointer } from '../../../services/world/world'

export type InstallFailure = { name: string; error: string }

const openRegistry = () => registryClient({ getAuthToken: () => '' })

/** The registry's Featured packages. */
export function featuredApps(): Promise<RegistryEntry[]> {
  return openRegistry().featured()
}

/** Install these entries' latest releases. Resolves with the ones that failed. */
export async function installFeaturedApps(
  repo: Repo,
  projectId: string,
  entries: RegistryEntry[],
): Promise<InstallFailure[]> {
  const registry = openRegistry()
  const worldId = readWorldPointer()
  const failed: InstallFailure[] = []
  // One at a time: two installs at once can both pass the type-clash check.
  for (const entry of entries) {
    try {
      const release = await registry.release(entry.latest.releaseId)
      await installRelease(
        repo as never,
        {
          projectId,
          worldId: worldId ?? undefined,
          scope: worldId ? 'world' : 'project',
        },
        release,
        entry.latest.releaseId,
      )
    } catch (cause) {
      failed.push({
        name: entry.name,
        error: cause instanceof Error ? cause.message : String(cause),
      })
    }
  }
  return failed
}
